'use client'

import { createContext, useContext, useRef, useCallback, ReactNode } from 'react'

interface LiveData {
  [key: string]: any
}

interface AIContextType {
  feedApiResponse: (endpoint: string, body: any, response: any) => void
  getLiveData: () => LiveData
  clearLiveData: () => void
}

const AIContext = createContext<AIContextType | undefined>(undefined)

export function AIProvider({ children }: { children: ReactNode }) {
  const liveDataRef = useRef<LiveData>({})

  const feedApiResponse = useCallback((endpoint: string, _body: any, response: any) => {
    if (!response || typeof response !== 'object') return

    const update: LiveData = {}

    // Sipariş detay
    if (endpoint.match(/\/order\/\d+/) && response?.id) {
      update.order_id = response.id
      update.status = response.status
      update.cargo_code = response.cargo_code ?? null
      update.invoice_id = response.invoice_id ?? null
      update.amount = response.amount
      update.customer_name = response.customer?.name
    }

    // Sipariş listesi
    if (endpoint.match(/\/order(\?|$)/) && Array.isArray(response?.items ?? response?.data)) {
      const items = response?.items ?? response?.data ?? []
      update.pending_count = items.filter((o: any) => o.status === 'bekliyor' || o.status === 'odendi').length
      update.error_count = items.filter((o: any) => o.status === 'hata').length
      update.total_orders = items.length
    }

    // Ürün listesi
    if (endpoint.includes('/product') && Array.isArray(response?.items ?? response?.data)) {
      const items = response?.items ?? response?.data ?? []
      update.out_of_stock_count = items.filter((p: any) => (p.stock ?? p.quantity) <= 0).length
      update.total_products = items.length
    }

    // Kargo listesi
    if (endpoint.includes('/cargo') && Array.isArray(response?.items ?? response?.data)) {
      const items = response?.items ?? response?.data ?? []
      update.stuck_count = items.filter((c: any) => c.stuck || c.status === 'takılı').length
    }

    // Fatura listesi
    if (endpoint.includes('/invoice') && Array.isArray(response?.items ?? response?.data)) {
      const items = response?.items ?? response?.data ?? []
      update.error_count = items.filter((i: any) => i.status === 'hata' || i.status === 'error').length
    }

    if (Object.keys(update).length > 0) {
      liveDataRef.current = { ...liveDataRef.current, ...update }
    }
  }, [])

  const getLiveData = useCallback(() => ({ ...liveDataRef.current }), [])

  const clearLiveData = useCallback(() => {
    liveDataRef.current = {}
  }, [])

  return (
    <AIContext.Provider value={{ feedApiResponse, getLiveData, clearLiveData }}>
      {children}
    </AIContext.Provider>
  )
}

export function useAIContext() {
  const ctx = useContext(AIContext)
  if (!ctx) throw new Error('useAIContext: AIProvider dışında kullanıldı')
  return ctx
}
